import { ExternalLink } from "@/components/ExternalLink";
import { Form } from "@/components/ui/form";
import { FormActionsNew, FormActionsUpdate } from "@/features/FormCard/Actions";
import { Diff } from "@/features/FormCard/Diff";
import {
  AutocompleteAsyncField,
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import { ConfirmationModal } from "@/features/FormCard/Modals";
import { NewDiff } from "@/features/FormCard/NewDiff";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { useEditForm } from "@/features/Edit/hooks";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { Button, Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getAuthorized } from "@/lib/fetch";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const client = "a6";
const table_name = "a6_events";
const static_data_table_name = "a6_static_data";

const formSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  event: z.string(),
  ratio: z.coerce.number(),
  a6_static_datum_id: z.number(),
  a6_static_datum_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      z.string().nullable().optional(),
      z.object({
        id: z.number(),
        text: z.string(),
      }),
    ]),
  ),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export function A6EventForm({
  id,
  isNew,
}: {
  id?: number;
  isNew: boolean;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const formApi = useEditForm({
    ...(isNew ? { isNew: true as const } : { id: id as number }),
    client,
    table_name,
    FormSchema: formSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "a6_static_datum_name") {
        return {
          id: fetchedData.a6_static_datum_id,
          text: value,
        };
      }
      if (key === "event") {
        return fetchedData.event;
      }
      return value;
    },
    adjustData: (data) => {
      const next = { ...data };
      delete next.a6_static_datum_name;
      return next;
    },
  });

  const staticDatumId = formApi.form.watch("a6_static_datum_id");

  const language = i18n.resolvedLanguage ?? "fr";
  const { data: translations } = useQuery({
    queryKey: [client, table_name, "translations", language],
    queryFn: () =>
      getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${client}/${table_name}/translations?language=${language}`
      ).then((r) => r.json()),
  });

  const eventOptions = useMemo(() => {
    const eventTranslations = ((translations?.event ?? {}) as Record<string, string>);
    return Object.entries(eventTranslations).map(([value, label]) => ({ value, label }));
  }, [translations]);

  return (
    <div className="bg-gray-50 h-full w-full flex items-center">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...formApi.form}>
            <form onSubmit={formApi.form.handleSubmit(formApi.onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: isNew ? "Events" : "Event",
                })}{" "}
                {isNew ? "" : id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <AutocompleteAsyncField
                  name="a6_static_datum_name"
                  label={t(`${table_name}.columns.a6_static_datum_name`, {
                    ns: "tables",
                    defaultValue: "Asset",
                  })}
                  control={formApi.form.control}
                  base_url={`${client}/${static_data_table_name}`}
                  id_column="a6_static_datum_id"
                  column_name="name"
                  isRequired
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={t(`${table_name}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    control={formApi.form.control}
                    isRequired
                  />
                  <SelectField
                    name="event"
                    label={t(`${table_name}.columns.event`, {
                      ns: "tables",
                      defaultValue: "Event type",
                    })}
                    control={formApi.form.control}
                    isRequired
                    options={eventOptions}
                  />
                </div>
                <InputField
                  name="ratio"
                  label={t(`${table_name}.columns.ratio`, {
                    ns: "tables",
                    defaultValue: "Ratio (before:after)",
                  })}
                  control={formApi.form.control}
                  type="number"
                  isRequired
                />
              </CardBody>
              <CardFooter>
                {isNew ? (
                  <FormActionsNew
                    onReset={formApi.handleReset}
                    onDelete={() => navigate(-1)}
                    isDirty={formApi.form.formState.isDirty}
                  />
                ) : (
                  <FormActionsUpdate
                    onReset={formApi.handleReset}
                    onDelete={formApi.openDeleteModal}
                    isDirty={formApi.form.formState.isDirty}
                    elementId={id as number}
                    client={client}
                  />
                )}
              </CardFooter>
            </form>
          </Form>
        </Card>
        {!isNew && staticDatumId ? (
          <div className="absolute top-14 right-14 2xl:top-10 2xl:right-10">
            <Button
              href={toWorkspacePath(
                `/admin/${client}/${static_data_table_name}/${staticDatumId}`,
              )}
              variant="bordered"
              color="primary"
              showAnchorIcon
              as={ExternalLink}
              radius="full"
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              {t("transactions.asset", { defaultValue: "Asset" })}
            </Button>
          </div>
        ) : null}
        {isNew ? (
          <ConfirmationModal
            isOpen={formApi.isConfirmationModalOpen}
            isLoading={formApi.isUpdating}
            onClose={formApi.cancelUpdate}
            onConfirm={formApi.confirmUpdate}
            title={t("confirm_update")}
          >
            {formApi.changedFields ? (
              NewDiff(formApi.changedFields, [], {}, table_name)
            ) : (
              t("no_changes")
            )}
          </ConfirmationModal>
        ) : (
          <>
            <ConfirmationModal
              isOpen={formApi.isConfirmationModalOpen}
              isLoading={formApi.isUpdating}
              onClose={formApi.cancelUpdate}
              onConfirm={formApi.confirmUpdate}
              title={t("confirm_update")}
            >
              {formApi.changedFields ? (
                Diff(formApi.changedFields, ["a6_static_datum_id"], {}, table_name)
              ) : (
                t("no_changes")
              )}
            </ConfirmationModal>
          </>
        )}
      </div>
    </div>
  );
}
